import type { WorkspacePublicId } from '@hookmyapp/shared';
import { UserBlockingError, ValidationError } from '@hookmyapp/observability/errors';
import { isValidPublicId } from '@/lib/public-id';
import { recordPendingState } from '@/lib/meta-popup-context';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

const POPUP_NAME = 'ig_login_signup';

function popupFeatures(): string {
  const w = 600;
  const h = 700;
  const left = Math.round((screen.width - w) / 2);
  const top = Math.round((screen.height - h) / 2);
  return `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`;
}

/**
 * Launch Instagram Login OAuth flow. Calls POST /instagram/oauth/start to mint
 * a server-side state row, then navigates the popup (or falls back to the same
 * window on mobile / popup-blocked) to the returned redirectUrl.
 *
 * Popup-blocker handling: window.open is the synchronous gated action — opened
 * immediately to about:blank, then navigated via popup.location.assign once the
 * mint resolves. This avoids the popup-blocker without requiring fresh user
 * activation on the navigate (MDN transient-activation).
 */
export async function launchInstagramSignup(
  workspaceTarget: WorkspacePublicId | (() => Promise<WorkspacePublicId>),
  fetchWithAuth: FetchWithAuth,
  opts?: { reconsent?: boolean },
): Promise<void> {
  if (typeof workspaceTarget !== 'function' && !isValidPublicId(workspaceTarget, 'ws')) {
    throw new ValidationError({
      message: `launchInstagramSignup: invalid workspacePublicId "${workspaceTarget}"`,
      code: 'INSTAGRAM_SIGNUP_INVALID_WORKSPACE_ID',
      details: { workspacePublicId: workspaceTarget },
    });
  }
  const isMobile = matchMedia('(max-width: 768px)').matches;
  const popup = isMobile ? null : window.open('about:blank', POPUP_NAME, popupFeatures());

  let redirectUrl: string;
  try {
    const workspacePublicId =
      typeof workspaceTarget === 'function' ? await workspaceTarget() : workspaceTarget;
    if (!isValidPublicId(workspacePublicId, 'ws')) {
      throw new ValidationError({
        message: `launchInstagramSignup: invalid workspacePublicId "${workspacePublicId}"`,
        code: 'INSTAGRAM_SIGNUP_INVALID_WORKSPACE_ID',
        details: { workspacePublicId },
      });
    }
    const res = await fetchWithAuth('/instagram/oauth/start', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspacePublicId,
      },
      body: JSON.stringify(opts?.reconsent ? { reconsent: true } : {}),
    });
    if (!res.ok) {
      throw new UserBlockingError({
        message: `/instagram/oauth/start failed: ${res.status}`,
        code: 'INSTAGRAM_OAUTH_START_FAIL',
        details: { status: res.status },
      });
    }
    const mint = (await res.json()) as { redirectUrl: string; state?: string };
    redirectUrl = mint.redirectUrl;
    // Mark this tab as the flow's owner so the localStorage relay in
    // useMetaSignupListeners only fires here, not in every open tab.
    if (mint.state) recordPendingState(mint.state);
  } catch (e) {
    popup?.close();
    throw e;
  }

  if (popup) {
    popup.location.assign(redirectUrl);
    return;
  }

  // Mobile or popup-blocker — fall through to same-window redirect.
  window.location.assign(redirectUrl);
}
